import { useMemo, useState } from 'react';
import { FULL_SUPPORT_LANGUAGES } from '@mews-commander/constants';
import { changeSessionLanguage, isLoggedInSession, useCurrentSession, useLanguageCode } from '@mews-commander/core';
import { fetchTexts, getTexts } from '@mews-commander/core/legacy-globalization';
import { Language } from '@mews-commander/data-access';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { DropdownItem, Select, Selected } from '@optimus-web/core';
import { useLanguages } from '../../../core/globalization';

export function LanguageSelector() {
    const currentSession = useCurrentSession();
    const dispatch = useCommanderThunkDispatch();
    const { options, isLoading: isLoadingOptions } = useLanguageOptions();
    const [isChanging, setChangingStatus] = useState(false);
    const currentLanguage = useLanguageCode();
    const currentTexts = useCommanderSelector<Record<string, unknown>>(getTexts);

    const handleChange = async (_: unknown, value: Selected) => {
        const languageCode = value as string;
        if (currentTexts[languageCode]) {
            changeSessionLanguage(languageCode);
            return;
        }

        setChangingStatus(true);
        await dispatch(fetchTexts(languageCode))
            .then(() => changeSessionLanguage(languageCode))
            .finally(() => setChangingStatus(false));
    };

    if (isLoggedInSession(currentSession)) {
        return null;
    }

    return (
        <Select
            name="language-selector"
            selected={currentLanguage}
            onChange={handleChange}
            loader={isChanging || isLoadingOptions}
        >
            {options.map((option) => (
                <DropdownItem key={option.value} {...option} />
            ))}
        </Select>
    );
}

function useLanguageOptions() {
    const { data: languages = [], isLoading } = useLanguages();
    const options = useMemo(
        () =>
            FULL_SUPPORT_LANGUAGES.map((languageCode) => getLanguageData(languages, languageCode))
                .filter((languageData) => languageData !== undefined)
                .map(({ code, localName }) => ({
                    value: code as string,
                    label: code === 'de-DE' ? 'Deutsch' : (localName as string),
                })),
        [languages]
    );

    return { options, isLoading };
}

function getLanguageData(languages: Language[], languageCode: string) {
    return languages.find((language) => language.code === languageCode);
}
